import { useMemo } from 'react';
import { useForm } from '../context';
import { calculatePaymentMethodDiscount } from '../helper';

// Rótulo da âncora "a partir de" por método (menor preço destacado no topo).
const ANCHOR_METHOD_LABELS: Record<string, string> = {
  credit_card: 'no cartão à vista',
  pix: 'no PIX',
  boleto: 'no boleto',
};

export function useHeaderPricing() {
  const { getTotal, items, isRecurrence, firstItem, installments, checkout, paymentMethods } =
    useForm();

  return useMemo(() => {
    const hasOrderBump = !!items.find((item) => item.type === 'order_bump');
    const totalValue = (isRecurrence ? firstItem.amount * firstItem.quantity : getTotal()) / 100;
    const maxInstallment = installments?.[installments?.length - 1]?.installment ?? 0;
    const installmentValue = installments?.[installments?.length - 1]?.value ?? 0;

    const hasEnrollmentFee = items.some((item) => item.type === 'enrollment_fee');
    const validItems = items.filter(
      (item) => !hasEnrollmentFee || ['enrollment_fee', 'order_bump'].includes(item.type)
    );

    const totalDiscountAmount =
      validItems.reduce((acc, item) => {
        if (item.couponItem) {
          return acc + (item.amount - item.couponItem.amount) * item.quantity;
        }
        return acc;
      }, 0) / 100;

    const enrollmentFee = items.find((item) => item.type === 'enrollment_fee');

    // Âncora "a partir de": menor preço entre os métodos disponíveis, já com o desconto por
    // forma de pagamento (no cartão vale o à vista = 1x). Vira a informação primária do topo,
    // coerente com a prateleira. Só quando a feature está configurada (há descontos).
    const discounts = checkout?.paymentMethodDiscounts ?? [];
    const methods = paymentMethods ?? [];
    let lowestPrice = 0;
    let lowestPriceMethodLabel = '';
    let otherMethodPrices: { method: string; net: number; label: string }[] = [];
    if (discounts.length > 0 && methods.length > 0) {
      const base = getTotal();
      // Todos os métodos COM desconto, do mais barato ao mais caro. O 1º é a âncora do header; os
      // demais são listados inline na mesma frase (estilo Mercado Livre), sem linha discreta solta.
      const priced = methods
        .map((method) => {
          const discount = calculatePaymentMethodDiscount(
            method,
            method === 'credit_card' ? 1 : undefined,
            items,
            discounts
          );
          return { method, discount, net: Math.max(base - discount, 0) };
        })
        .filter((price) => price.discount > 0)
        .sort((a, b) => a.net - b.net);

      if (priced.length > 0) {
        lowestPrice = priced[0].net / 100;
        lowestPriceMethodLabel = ANCHOR_METHOD_LABELS[priced[0].method] ?? '';
        otherMethodPrices = priced.slice(1).map((price) => ({
          method: price.method,
          net: price.net / 100,
          label: ANCHOR_METHOD_LABELS[price.method] ?? '',
        }));
      }
    }
    const hasPaymentMethodDiscount = lowestPrice > 0;

    return {
      hasOrderBump,
      totalValue,
      maxInstallment,
      installmentValue,
      hasEnrollmentFee,
      totalDiscountAmount,
      enrollmentFee,
      getTotal,
      firstItem,
      isRecurrence,
      hasPaymentMethodDiscount,
      lowestPrice,
      lowestPriceMethodLabel,
      otherMethodPrices,
    };
  }, [items, firstItem, isRecurrence, installments, getTotal, checkout, paymentMethods]);
}
